import { ClockIcon } from '@heroicons/react/24/outline'
import { useRef } from 'react'
import type { Color } from 'stories/types'

interface InputProps {
  /**
   * What background color to use
   */
  color?: Color
  /**
   * rows
   */
  rows?: number
  /**
   * Is Full
   */
  full?: boolean
  /**
   * Is disabled
   */
  disabled?: boolean
  /**
   * Is autofocus
   */
  autoFocus?: boolean
  /**
   * Title of Input
   */
  title?: string
  /**
   * Placeholder of Input
   */
  placeholder?: string
  /**
   * Name of Input
   */
  name?: string
  /**
   * Value of Input
   */
  value?: string
  /**
   * Error of Input
   */
  error?: string
  /**
   * Custom class name
   */
  className?: string
  /**
   * Is has icon
   */
  required?: boolean
  /**
   * Icon component
   */
  prefix?: string
  /**
   * onChange
   */
  history?: boolean
  /**
   * Icon component
   */
  onChange: (e: any) => void // string | React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: () => void
  showHistory?: () => void
  onKeyPress?: (e: any) => void
}

/**
 * Primary UI component for user interaction
 */
export const TextArea = ({
  rows = 4,
  color = 'sky',
  disabled = false,
  autoFocus = false,
  title = '',
  placeholder = ' ',
  name = '',
  value = '',
  error = '',
  className = '',
  prefix = '',
  history = false,
  required = false,
  onChange = () => {},
  showHistory = () => {},
  onBlur = () => {},
  ...props
}: InputProps) => {
  const classNames = [
    'block',
    'rounded-t',
    'px-2.5',
    'pb-[2px]',
    title ? 'pt-[27px]' : 'pb-[2px]',
    'w-full',
    'text-[15px]',
    'text-gray-900',
    'border',
    'border-gray-300',
    'focus:outline-none',
    'focus:ring-0',
    `focus:border-${color}-600`,
    'peer',
    prefix.length > 0 && 'pl-7',
    error && 'border-rose-700',
    disabled ? 'cursor-not-allowed bg-gray-100' : 'bg-white',
  ]

  const inputRef = useRef<HTMLTextAreaElement>(null)

  const changeInput = (e: any) => {
    const _value = e.target.value
    onChange(_value)
    setTimeout(() => {
      if (document.activeElement === inputRef.current) {
        if (inputRef?.current?.value === _value) {
          inputRef?.current?.blur()
        }
      }
    }, 5000)
  }

  return (
    <div className="input-container">
      <div className={`group relative z-0 w-full ${className}`}>
        {prefix.length > 0 && (
          <div className="text-gray-600 text-[15px] flex absolute top-[28px] left-0 items-center pl-3 pointer-events-none">
            {prefix}
          </div>
        )}
        <textarea
          rows={rows}
          name={name}
          className={classNames.join(' ')}
          placeholder={placeholder}
          ref={inputRef}
          disabled={disabled}
          autoFocus={autoFocus}
          value={value}
          required={required}
          onChange={changeInput}
          onBlur={() => onBlur()}
          {...props}
        />
        {title && (
          <label
            htmlFor={name}
            className="absolute text-[12px] text-gray-700 top-2.5 border-b z-10 origin-[0] left-2.5 flex gap-2 items-center"
          >
            {title}
            {required && '*'}
            {history && (
              <span className="ml-1 hidden group-hover:inline" onClick={() => showHistory()}>
                <ClockIcon className="h-[14px] w-[14px] text-gray-500 cursor-pointer" aria-hidden="true" />
              </span>
            )}
          </label>
        )}
      </div>
      {error && <p className="peer-invalid:visible text-rose-700 text-[13px] pl-2 pt-[1px]">{error}</p>}
    </div>
  )
}
